import { useMemo } from 'react';
import { useAdminFormPage } from '../../hooks/useAdminFormPage';
import { AdminFormPage } from '../../components/AdminFormPage';
import { getErpRegistryFormConfig } from '../../configs/erpRegistryFormConfig';
import { erpRegistriesService } from '../../api/erpRegistriesService';
import { parseJsonField } from '../../utils/parseJsonField';
import type { CreateErpRegistryPayload, UpdateErpRegistryPayload } from '../../api/erpRegistriesService';

interface RegistryParams {
  slug?: string;
}

export function RegistryFormPage() {
  const formPage = useAdminFormPage<unknown, RegistryParams>({
    entityName: 'registry',
    getConfig: (mode, params) => getErpRegistryFormConfig(mode, params.slug),
    getEditId: (params) => params.slug,
    queryKey: (params) => ['admin', 'erp', 'registries', params.slug],
    fetchFn: (params) => {
      if (!params.slug) {
        return Promise.reject(new Error('Registry slug is required'));
      }
      return erpRegistriesService.get(params.slug);
    },
    mapToInitialValues: (entity) => {
      const reg = entity as Record<string, unknown>;
      return {
        slug: reg.slug,
        name: reg.name,
        base_url: reg.base_url,
        auth_type: reg.auth_type,
        auth_config: reg.auth_config ? JSON.stringify(reg.auth_config, null, 2) : '',
        default_headers: reg.default_headers ? JSON.stringify(reg.default_headers, null, 2) : '',
        timeout_ms: reg.timeout_ms,
        retry_policy: reg.retry_policy ? JSON.stringify(reg.retry_policy, null, 2) : '',
        is_active: reg.is_active,
        is_main: reg.is_main ?? false,
      };
    },
    mutationFn: async (payload, isEdit, params) => {
      const authConfig = parseJsonField(payload.auth_config);
      const defaultHeaders = parseJsonField(payload.default_headers);
      const retryPolicy = parseJsonField(payload.retry_policy);

      const cleanedPayload = {
        ...payload,
        auth_config: authConfig ?? {},
        default_headers: defaultHeaders,
        retry_policy: retryPolicy,
        timeout_ms: payload.timeout_ms ? Number(payload.timeout_ms) : undefined,
      };

      if (isEdit && params.slug) {
        return erpRegistriesService.update(params.slug, cleanedPayload as UpdateErpRegistryPayload);
      }
      return erpRegistriesService.create(cleanedPayload as CreateErpRegistryPayload);
    },
    invalidateKey: () => ['admin', 'erp', 'registries'],
    navigateBackPath: () => '/admin/erp/registries',
  });

  const breadcrumbs = useMemo(() => [
    { label: 'Admin', href: '/admin' },
    { label: 'ERP Registries', href: '/admin/erp/registries' },
    { label: formPage.isEdit ? 'Edit Registry' : 'Create Registry' },
  ], [formPage.isEdit]);

  const errorBreadcrumbs = useMemo(() => [
    { label: 'Admin', href: '/admin' },
    { label: 'ERP Registries', href: '/admin/erp/registries' },
    { label: 'Edit' },
  ], []);

  return (
    <AdminFormPage
      formPage={formPage}
      entityLabel="Registry"
      breadcrumbs={breadcrumbs}
      errorBreadcrumbs={errorBreadcrumbs}
      genericFetchErrorMessage="An unexpected error occurred."
    />
  );
}
